import React, { Suspense, use, useEffect, useMemo, useState } from 'react'
import { Provider } from 'react-redux'
import { BrowserRouter, HashRouter, useLocation } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { ModalProvider } from '../components/modal-provider/ModalProvider'
import SuspensePanel from '../components/suspense'
import store from '../store'
import { setI18nStore } from '../locale/i18n'
import { loadWalletRoot } from './walletRootLoader'

const getRouterComponent = () => {
  if (import.meta.env.VITE_BROWSER_HISTORY) {
    return BrowserRouter
  } else {
    return HashRouter
  }
}

/**
 * Route prefixes that need the wagmi/Reown/lifi stack mounted.
 *
 * Anything outside this list (feed, market detail, about, profile, tags,
 * markets list, etc.) renders without WalletRoot, which keeps `wagmi`,
 * `viem`, `@reown/appkit*`, `@lifi/sdk` and the per-chain chunks
 * (`arbitrumSepolia`, `walletConnect`, `secp256k1`, `parseSignature`,
 * `WebCryptoP256`, …) off the critical path on those pages.
 *
 * Login / register (`/l/...`) are intentionally omitted: that UI is a light
 * modal. Wagmi/Reown load only if the user clicks “login with wallet”.
 *
 * Entries match against the pathname after the optional locale prefix
 * (e.g. `/pt-br/...`).
 */
const WALLET_ROUTE_PREFIXES = [
  '/a/settings',
  '/p/wallet',
  '/p/deposit',
  '/p/withdraw',
  '/p/personal-info-update',
  '/p/update-password',
  '/p/email-update',
  '/p/complete-your-profile',  // SIWE / Magic post-signin flow
  // '/m/cash-in' is intentionally absent: the gate swap here changes the tree
  // above the whole app and remounts it, so the deposit modal manages wagmi
  // itself — currently not at all (wallet path gated off), and via its own
  // WalletRootProvider when WALLET_PATH_ENABLED is restored (see ViewDeposit).
  '/m/withdraw',
  '/m/kyc',
  '/m/redeem-code',
]

const stripLocalePrefix = (pathname) =>
  pathname.replace(/^\/(?:pt-br|en)(?=\/|$)/, '') || '/'

export const isWalletRoute = (pathname) => {
  if (!pathname) return false
  const stripped = stripLocalePrefix(pathname)
  return WALLET_ROUTE_PREFIXES.some((prefix) => stripped.startsWith(prefix))
}

export function WalletRootProvider({ children }) {
  const WalletRoot = use(loadWalletRoot())
  return <WalletRoot>{children}</WalletRoot>
}

/**
 * Lazily mounts ReownWagmiRoot the first time the user lands on a wallet
 * route. We use a "load once and keep" pattern so wagmi state survives
 * navigations like /l/login → /q/123 → /p/wallet (the user stays connected).
 *
 * On a wallet route the children render only AFTER WalletRoot has loaded,
 * because they call wagmi hooks (`useAccount`, `useDisconnect`, …) that
 * require `WagmiProvider` in the React tree. On non-wallet routes children
 * render immediately and WalletRoot stays out of the bundle.
 *
 * While the wallet chunk is in flight we *suspend* instead of rendering
 * `null`. React Router wraps navigations in startTransition, so Suspense
 * keeps the previous page on screen instead of blanking the tree.
 */
function WalletGate({ ssr, children }) {
  const location = useLocation()
  // A modal route (e.g. /m/cash-in) renders the last non-modal page underneath
  // it, so that background page's wallet needs count too — otherwise a fresh
  // mount at the modal URL leaves a wagmi-dependent page without its provider.
  const needsWallet = useMemo(() => {
    if (isWalletRoute(location.pathname)) return true
    if (typeof sessionStorage === 'undefined') return false
    const background = (sessionStorage.getItem('lastNonModalPath') || '').split('?')[0]
    return isWalletRoute(background)
  }, [location.pathname])
  const [keepMounted, setKeepMounted] = useState(false)

  useEffect(() => {
    if (ssr) return
    if (needsWallet) setKeepMounted(true)
  }, [ssr, needsWallet])

  if (ssr) return children
  if (needsWallet || keepMounted) {
    return <WalletRootProvider>{children}</WalletRootProvider>
  }
  return children
}

/**
 * Compose multiple providers into a single component to avoid deep nesting.
 * When ssr is true, uses StaticRouter with routerProps and skips Wagmi/AppKit (browser-only).
 */
const AppProviders = ({ children, routerProps = {}, ssr = false, RouterComponent: RouterOverride, store: storeOverride, helmetContext }) => {
  const RouterComponent = RouterOverride || getRouterComponent()
  const appStore = storeOverride || store

  useEffect(() => {
    setI18nStore(appStore)
  }, [appStore])

  const inner = (
    <HelmetProvider context={helmetContext}>
      <ModalProvider>
        {ssr ? (
          children
        ) : (
          <Suspense fallback={<SuspensePanel page />}>
            <WalletGate ssr={ssr}>{children}</WalletGate>
          </Suspense>
        )}
      </ModalProvider>
    </HelmetProvider>
  )

  return (
    <Provider store={appStore}>
      <RouterComponent {...routerProps}>
        {inner}
      </RouterComponent>
    </Provider>
  )
}

export default AppProviders
